import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Dropdown,
  MetricCard,
  Spinner,
  useLocalStorage,
} from '@makeably/creativex-design-system';
import {
  dateOptionStorageKey,
  filterStorageKey,
} from 'components/creative_campaigns/shared';
import { getOptions } from 'components/molecules/FilterButton';
import FilterWithTags from 'components/molecules/FilterWithTags';
import ItemsTable from 'components/molecules/ItemsTable';
import { addToast } from 'components/organisms/Toasts';
import { getPlacementLabel } from 'components/reporting/creativeLifecycleUtilities';
import {
  attributeLookupsProps,
  dateOptionProps,
} from 'components/reporting/shared';
import { getObjFilterTest } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import { setItemElement } from 'utilities/itemElement';
import { useViewPage } from 'utilities/mixpanel';
import { rate } from 'utilities/number';
import {
  objectGroupBy,
  removeProperty,
} from 'utilities/object';
import { get } from 'utilities/requests';
import {
  creativeCampaignPath,
  dashboardRecordsCreativeCampaignsPath,
} from 'utilities/routes';
import {
  toConciseCount,
  toConciseSpend,
  toPercent,
} from 'utilities/string';
import styles from './Dashboard.module.css';

const CAMPAIGNS_PER_PAGE = 25;

const propTypes = {
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  initialDateOption: dateOptionProps.isRequired,
  lookups: attributeLookupsProps.isRequired,
  scoreDetails: PropTypes.shape({
    id: PropTypes.number,
    rateLabel: PropTypes.string,
    spendRateLabel: PropTypes.string,
  }).isRequired,
};

const segments = [
  {
    label: 'Ad Format',
    value: 'adFormat',
  },
  {
    label: 'Brand',
    value: 'brand',
  },
  {
    label: 'Channel',
    value: 'channel',
  },
  {
    label: 'Creative Campaign Name',
    value: 'name',
  },
  {
    label: 'Creator Type',
    value: 'contentType',
  },
  {
    label: 'Market',
    value: 'market',
  },
  {
    label: 'Placement',
    value: 'platformPlacements',
  },
];

function getHeaders(scoreDetails) {
  return [
    {
      className: styles.nameHeader,
      key: 'name',
      label: 'Creative Campaign Name',
    },
    {
      key: 'spend',
      label: 'Spend',
    },
    {
      key: `scoreRate::${scoreDetails.id}`,
      label: scoreDetails.rateLabel,
    },
    {
      key: `spendRate::${scoreDetails.id}`,
      label: scoreDetails.spendRateLabel,
    },
    {
      key: 'markets',
      label: 'Markets',
    },
    {
      key: 'posts',
      label: 'Posts',
    },
    {
      key: 'channels',
      label: 'Channels',
    },
    {
      key: 'adFormats',
      label: 'Ad\nFormats',
    },
    {
      key: 'placements',
      label: 'Placements',
    },
    {
      key: 'creatorTypes',
      label: 'Creator\nTypes',
    },
    {
      key: 'impressions',
      label: 'Impressions',
    },
  ];
}

function getMetrics(scoreDetails) {
  return [
    {
      label: 'Spend',
      value: 'spend',
    },
    {
      label: scoreDetails.rateLabel,
      value: `scoreRate::${scoreDetails.id}`,
    },
    {
      label: scoreDetails.spendRateLabel,
      value: `spendRate::${scoreDetails.id}`,
    },
    {
      label: 'Brands',
      value: 'brands',
    },
    {
      label: 'Markets',
      value: 'markets',
    },
    {
      label: 'Creative Campaigns',
      value: 'creativeCampaigns',
    },
    {
      label: 'Posts',
      value: 'posts',
    },
  ];
}

async function getRecords(dateOption) {
  const params = {
    date_type: dateOption.type,
    start_date: dateOption.startDate,
  };

  return get(dashboardRecordsCreativeCampaignsPath(params));
}

function processRecord(record, lookups) {
  let platformPlacements = [];
  if (Array.isArray(record.placements)) {
    platformPlacements = record.placements.map((p) => getPlacementLabel(p.publisher, p.placement));
  }

  return {
    ...record,
    adFormat: lookups.adFormat[record.adFormatKey],
    brand: lookups.brand[record.brandId],
    channel: lookups.channel[record.channelId],
    market: lookups.market[record.marketId],
    platformPlacements,
  };
}

function getCampaignDetails(recordGroup, scoreId) {
  const campaignName = recordGroup[0].name;
  const { id } = recordGroup[0];

  const data = recordGroup.reduce(
    ({
      spend,
      qualitySpend,
      qualityPostCount,
      postCount,
      marketSet,
      channelSet,
      adFormatSet,
      placementSet,
      creatorTypeSet,
      impressions,
    },
    record) => {
      const qualityPost = record[`rank::${scoreId}`] === 'highest';

      return {
        adFormatSet: record.adFormat ? new Set([...adFormatSet, record.adFormat]) : adFormatSet,
        channelSet: record.channel ? new Set([...channelSet, record.channel]) : channelSet,
        creatorTypeSet:
          record.contentType ? new Set([...creatorTypeSet, record.contentType]) : creatorTypeSet,
        impressions: impressions + (record.impressions ?? 0),
        marketSet: record.market ? new Set([...marketSet, record.market]) : marketSet,
        placementSet: new Set([...placementSet, ...(record.platformPlacements ?? [])]),
        postCount: postCount + (record.postCount ?? 0),
        qualityPostCount: qualityPostCount + (qualityPost ? record.postCount : 0),
        qualitySpend: qualitySpend + (record[`excellentSpend::${scoreId}`] ?? 0),
        spend: spend + (record.spend ?? 0),
      };
    },
    {
      adFormatSet: new Set(),
      channelSet: new Set(),
      creatorTypeSet: new Set(),
      impressions: 0,
      marketSet: new Set(),
      placementSet: new Set(),
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );

  const scoreRate = rate(data.qualityPostCount, data.postCount);
  const spendRate = rate(data.qualitySpend, data.spend);

  return {
    adFormats: {
      value: data.adFormatSet.size,
    },
    channels: {
      value: data.channelSet.size,
    },
    creatorTypes: {
      value: data.creatorTypeSet.size,
    },
    id: { value: id },
    impressions: {
      label: toConciseCount(data.impressions),
      value: data.impressions,
    },
    markets: {
      value: data.marketSet.size,
    },
    name: {
      display: { url: creativeCampaignPath(id) },
      value: campaignName,
    },
    placements: {
      value: data.placementSet.size,
    },
    posts: {
      label: toConciseCount(data.postCount),
      value: data.postCount,
    },
    [`scoreRate::${scoreId}`]: {
      label: toPercent(scoreRate, 1) ?? 'N/A',
      value: scoreRate,
    },
    spend: {
      label: toConciseSpend(data.spend),
      value: data.spend,
    },
    [`spendRate::${scoreId}`]: {
      label: toPercent(spendRate, 1) ?? 'N/A',
      value: spendRate,
    },
  };
}

function getItems(records, scoreId) {
  const groupedRecords = objectGroupBy(records, (record) => record.name);

  return Object.values(groupedRecords).map((group) => (
    setItemElement(getCampaignDetails(group, scoreId))
  ));
}

function getMetricTotals(processedRecords, scoreId) {
  return processedRecords.reduce(
    (acc, record) => {
      acc.spend += record.spend ?? 0;
      acc.qualitySpend += record[`excellentSpend::${scoreId}`] ?? 0;
      acc.qualityPostCount += (record[`rank::${scoreId}`] === 'highest' ? record.postCount : 0);
      acc.postCount += record.postCount ?? 0;
      if (record.brand) acc.brandSet.add(record.brand);
      if (record.market) acc.marketSet.add(record.market);
      if (record.name) acc.campaignSet.add(record.name);
      return acc;
    },
    {
      brandSet: new Set(),
      campaignSet: new Set(),
      marketSet: new Set(),
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );
}

function calculateMetrics(records, scoreDetails) {
  const totals = getMetricTotals(records, scoreDetails.id);

  const scoreRate = totals.postCount ? totals.qualityPostCount / totals.postCount : 0;
  const spendRate = totals.spend ? totals.qualitySpend / totals.spend : 0;

  return {
    brands: totals.brandSet.size,
    creativeCampaigns: totals.campaignSet.size,
    markets: totals.marketSet.size,
    posts: totals.postCount,
    spend: totals.spend,
    [`scoreRate::${scoreDetails.id}`]: scoreRate,
    [`spendRate::${scoreDetails.id}`]: spendRate,
  };
}

function getMetricValue(metric, processedRecords, filteredMetrics) {
  if (!processedRecords.length) return 'No data';

  const { value } = metric;
  const metricValue = filteredMetrics[value];

  switch (value) {
    case 'spend':
      return toConciseSpend(metricValue);
    case 'creativeCampaigns':
    case 'posts':
      return toConciseCount(metricValue);
    case 'brands':
    case 'markets':
      return metricValue;
    default:
      return toPercent(metricValue, 1);
  }
}

function getCaption(metric, filteredValue, overallValue) {
  const percentMetrics = ['spend', 'markets', 'creativeCampaigns', 'posts'];
  if (percentMetrics.includes(metric)) {
    const percent = overallValue ? (filteredValue / overallValue) : 0;
    return `${toPercent(percent, 0, true)} of total`;
  }
  if (metric === 'brands') {
    return `${overallValue} total`;
  }
  return null;
}

function renderEmptyContent() {
  return (
    <div className="t-empty">
      <p>No data available</p>
    </div>
  );
}

function Dashboard({
  dateOptions,
  initialDateOption,
  lookups,
  scoreDetails,
}) {
  useViewPage();

  const [records, setRecords] = useState([]);
  const [selectedDateOption, setSelectedDateOption] = useLocalStorage(
    dateOptionStorageKey,
    initialDateOption,
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useLocalStorage(filterStorageKey, {});
  const [sort, setSort] = useState({
    asc: false,
    key: 'spend',
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const hasFilters = Object.keys(filters).length > 0;

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const response = await getRecords(selectedDateOption);

        if (response.isError) {
          const isTimeout = response.status === 504;
          const message = isTimeout ? 'The data request has timed out' : 'The data could not be loaded';
          addToast(message, { type: 'error' });
        } else {
          setRecords(response.data);
        }
      } catch (error) {
        addToast('An unexpected error occurred', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [selectedDateOption]);

  const processedRecords = useMemo(
    () => records.map((record) => processRecord(record, lookups)), [records, lookups],
  );

  const overallMetrics = useMemo(
    () => calculateMetrics(processedRecords, scoreDetails), [processedRecords, scoreDetails],
  );

  const filterOptions = useMemo(() => getOptions(processedRecords, segments), [processedRecords]);

  const filteredRecords = useMemo(
    () => {
      if (hasFilters) {
        const filterTest = getObjFilterTest(filters);
        return processedRecords.filter(filterTest);
      }
      return processedRecords;
    },
    [processedRecords, filters, hasFilters],
  );

  const filteredMetrics = useMemo(
    () => {
      if (hasFilters) {
        return calculateMetrics(filteredRecords, scoreDetails);
      }
      return overallMetrics;
    },
    [filteredRecords, scoreDetails, hasFilters, overallMetrics],
  );

  const sortedItems = useMemo(() => {
    const items = getItems(filteredRecords, scoreDetails.id);
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return items.slice().sort(byKeyDir);
  }, [filteredRecords, scoreDetails, sort]);

  const handleFilterChange = (selections, keyToRemove = '') => {
    setPage(1);
    if (keyToRemove) {
      setFilters((last) => removeProperty(last, keyToRemove));
    } else {
      setFilters(selections);
    }
  };

  const renderMetricCard = (metric) => {
    const value = getMetricValue(metric, processedRecords, filteredMetrics);
    let caption = null;

    if (hasFilters && processedRecords.length > 0) {
      caption = getCaption(
        metric.value,
        filteredMetrics[metric.value],
        overallMetrics[metric.value],
      );
    }

    return (
      <div key={metric.value} className={styles.metricCard}>
        <MetricCard
          alert={false}
          metric={{
            ...metric,
            caption,
            value,
          }}
        />
      </div>
    );
  };

  const renderMetricCards = () => {
    if (processedRecords.length > 0) {
      return getMetrics(scoreDetails).map(renderMetricCard);
    }
    return renderEmptyContent();
  };

  if (loading) {
    return (
      <div className="u-flexColumn u-alignCenter u-gap-16">
        <Spinner />
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="u-flexColumn u-gap-16">
      <div className="u-flexRow u-justifyBetween">
        <FilterWithTags
          filterOpen={filterOpen}
          filterOptions={filterOptions}
          filters={filters}
          segments={segments}
          onClose={() => setFilterOpen(false)}
          onFilterChange={handleFilterChange}
          onOpen={() => setFilterOpen(true)}
        />
        <Dropdown
          menuProps={{ size: 'medium' }}
          options={dateOptions}
          selected={selectedDateOption}
          size="small"
          onChange={setSelectedDateOption}
        />
      </div>
      <Card className="u-flexColumn u-gap-16">
        <h5>Overview</h5>
        <div className="u-flexRow u-justifyBetween">
          { renderMetricCards() }
        </div>
      </Card>
      <Card className="u-flexColumn u-gap-24">
        <h5>Creative Campaigns</h5>
        <ItemsTable
          className={`u-scrollShadowRight ${styles.table}`}
          emptyTableContent={renderEmptyContent()}
          headers={getHeaders(scoreDetails)}
          items={sortedItems}
          maxWidthSize="custom"
          page={page}
          perPage={CAMPAIGNS_PER_PAGE}
          sort={sort}
          breakTooltip
          onPageChange={setPage}
          onSortChange={setSort}
        />
      </Card>
    </div>
  );
}

Dashboard.propTypes = propTypes;

export default Dashboard;
